import { useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import useLanguageChange from "../../hooks/useLanguageChange";
import { setIsTranslationPanelOpen, setIsAutoTranslateEnabled, setSelectedTranslationLanguage } from "../../reducers/chat-reducer";
import { TRANSLATION_LANGUAGES, getTranslationLanguageByCode, getAppLanguageCode } from "../../constants/TranslationConstants";
import { LogFirebase } from "../../services/firestore";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY } from "../../constants/FirebaseConstants";

const TranslationSettingsPanel = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();
    const hasInitializedLanguage = useRef(false);

    const { isTranslationPanelOpen, isAutoTranslateEnabled, selectedTranslationLanguage } = useSelector((state) => state.chatReducer);
    const { endCallData } = useSelector((state) => state.endCall);

    // Reset initialization flag when call ends so next call syncs with app language
    useEffect(() => {
        if (endCallData) {
            hasInitializedLanguage.current = false;
        }
    }, [endCallData]);

    // Sync translation language with app language only on first panel open during the call
    useEffect(() => {
        if (isTranslationPanelOpen && !hasInitializedLanguage.current) {
            hasInitializedLanguage.current = true;
            const appLanguageCode = getAppLanguageCode();
            if (appLanguageCode !== selectedTranslationLanguage) {
                dispatch(setSelectedTranslationLanguage(appLanguageCode));
            }
        }
    }, [isTranslationPanelOpen, dispatch, selectedTranslationLanguage]);

    const selectedLanguageObj = useMemo(() => {
        return getTranslationLanguageByCode(selectedTranslationLanguage) || TRANSLATION_LANGUAGES[0];
    }, [selectedTranslationLanguage]);

    const handleToggleAutoTranslate = () => {
        dispatch(setIsAutoTranslateEnabled(!isAutoTranslateEnabled));
    };

    const handleLanguageChange = (languageCode) => {
        dispatch(setSelectedTranslationLanguage(languageCode));
    };

    const handleDone = () => {
        LogFirebase(FIREBASE_EVENT_CATEGORY.ChatTranslation, FIREBASE_LOG_KEY.TranslationSettingsChanged, {
            autoTranslateEnabled: isAutoTranslateEnabled,
            selectedLanguage: selectedTranslationLanguage,
        });
        dispatch(setIsTranslationPanelOpen(false));
    };

    if (!isTranslationPanelOpen) {
        return null;
    }

    return (
        <div className='translation-settings-panel'>
            <div className='translation-settings-panel-in'>
                {/* Auto-Translate Toggle */}
                <div className='translation-setting-row auto-translate-row'>
                    <div className='translation-setting-label'>
                        <span className='for-icon translate-icon-text'>{`A\u6587`}</span>
                        <span className='label-text'>{localisedValues["auto_translate"]}</span>
                    </div>
                    <div className='translation-setting-control'>
                        <label className='switch-toggle'>
                            <input
                                type='checkbox'
                                checked={isAutoTranslateEnabled}
                                onChange={handleToggleAutoTranslate}
                                data-testid='auto-translate-toggle'
                            />
                            <span className='switch-slider'></span>
                        </label>
                    </div>
                </div>

                {/* Language Selection */}
                <div className='translation-setting-row language-select-row'>
                    <div className='translation-setting-label'>
                        <span className='label-text'>{localisedValues["your_language"]}</span>
                    </div>
                    <div className='translation-setting-control'>
                        <div className='dropdown translation-language-dropdown'>
                            <button
                                className='btn dropdown-toggle translation-dropdown-btn'
                                type='button'
                                id='translation-language-dropdown'
                                data-bs-toggle='dropdown'
                                aria-expanded='false'
                                data-testid='translation-language-dropdown'
                            >
                                <div className='available-language-box'>
                                    <div className={clsx("flag-icon", selectedLanguageObj.class)}></div>
                                    <div className='language-name'>
                                        <div className='country-language'>{selectedLanguageObj.languageName}</div>
                                    </div>
                                </div>
                                <span className='dropdown-arrow icon-down-arrow-light'></span>
                            </button>
                            <div className='dropdown-menu' aria-labelledby='translation-language-dropdown'>
                                <div className='dropdown-menu-in available-language-list custom-scroll-thin'>
                                    <ul>
                                        {TRANSLATION_LANGUAGES.map((language) => (
                                            <li key={language.code}>
                                                <div className='dropdown-item-div'>
                                                    <button
                                                        className={clsx("dropdown-item", language.code === selectedTranslationLanguage && "active")}
                                                        onClick={() => handleLanguageChange(language.code)}
                                                        data-testid={`lang-option-${language.code}`}
                                                    >
                                                        <div className='available-language-box'>
                                                            <div className={clsx("flag-icon", language.class)}></div>
                                                            <div className='language-name'>
                                                                <div className='country-language'>{language.languageName}</div>
                                                                <div className='country-name'>{language.countryName}</div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Info Block */}
                <div className='translation-info-block'>
                    <span className='info-icon icon-info'></span>
                    <p className='info-text'>
                        {`Messages from other participants will be automatically translated to ${selectedLanguageObj.languageName}.`}
                    </p>
                </div>

                {/* Done Button */}
                <div className='translation-done-btn-wrapper'>
                    <button className='btn btn-primary translation-done-btn' onClick={handleDone} data-testid='translation-done-btn'>
                        {localisedValues["done"]}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TranslationSettingsPanel;
